<script setup lang="ts">
import { computed } from 'vue';

export type AdvantageCard = {
  imageUrl: string
  imageSrcset?: string
  imageAlt?: string
  imageWidth?: number | null
  imageHeight?: number | null
  cardBg?: string
  headingHtml?: string
  captionPlain?: string
};

const props = withDefaults(
  defineProps<{
    sectionBg?: string;
    headingHtml?: string;
    cards?: AdvantageCard[] | string;
  }>(),
  {
    sectionBg: '',
    headingHtml: '',
    cards: () => [],
  },
);

function parseCards(raw: AdvantageCard[] | string | undefined): AdvantageCard[] {
  if (raw === undefined || raw === null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (s === '' || s === '[]') return [];
    try {
      const parsed = JSON.parse(s) as unknown;
      return Array.isArray(parsed) ? (parsed as AdvantageCard[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

const cardsList = computed((): AdvantageCard[] => parseCards(props.cards));

const show = computed(() => {
  const h = props.headingHtml ?? '';
  const t = h.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  return t.length > 0 || cardsList.value.length > 0;
});

function cardHeadingPlain(html: string | undefined): boolean {
  if (!html) return false;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length > 0;
}
</script>

<template>
  <section
    v-if="show"
    class="advantages-section font-inter"
  >
    <div class="container">
      <div
        v-if="headingHtml"
        class="leading-140 md:leading-115 mb-32 max-w-4xl text-30 font-semibold tracking-tight text-off-black-500 md:mb-40 md:text-32 lg:mb-48 lg:text-40 lg:leading-110 xl:text-40 [&_p]:m-0"
        v-html="headingHtml"
      />
      <div
        v-if="cardsList.length"
        class="grid grid-cols-1 gap-y-24 md:grid-cols-2 md:gap-24 xl:grid-cols-4 xl:gap-28"
      >
        <article
          v-for="(card, index) in cardsList"
          :key="index"
          class="mb-0 flex min-w-0 flex-col"
        >
          <div
            class="relative max-lg:h-[240px] max-lg:max-h-[240px] w-full shrink-0 overflow-hidden rounded-2xl lg:h-[300px] lg:max-h-[300px] lg:min-h-[300px]"
            :style="card.cardBg ? { backgroundColor: card.cardBg } : undefined"
          >
            <img
              v-if="card.imageUrl"
              :src="card.imageUrl"
              :srcset="card.imageSrcset || undefined"
              :alt="card.imageAlt || ''"
              :width="card.imageWidth ?? undefined"
              :height="card.imageHeight ?? undefined"
              class="absolute inset-0 size-full origin-center scale-[1] object-cover object-center motion-reduce:scale-100"
              loading="lazy"
              decoding="async"
            >
          </div>
          <div
            v-if="cardHeadingPlain(card.headingHtml)"
            class="leading-140 mt-12 text-left text-14 font-semibold text-off-black-500 md:mt-16 md:text-15 lg:text-16"
            v-html="card.headingHtml"
          />
          <p
            v-else-if="card.captionPlain?.trim()"
            class="leading-140 mt-12 font-inter-tight text-left text-18 font-medium text-off-black-500 md:mt-16 lg:text-20 lg:max-w-[240px]"
          >
            {{ card.captionPlain.trim() }}
          </p>
        </article>
      </div>
    </div>
  </section>
</template>
